/**
 * Legacy `.xls` (OLE2 / BIFF) support — the format `read-excel-file` deliberately refuses.
 *
 * The reasoning, and why this is a SECOND parser rather than a replacement, is in this module's
 * test. In short: `.xlsx` keeps its existing path (which also extracts images and merge/column
 * layout, and is 7.5MB lighter); this one exists so a real requisition that happens to be in the
 * old binary format can be read instead of handed back to the purchaser to re-save.
 *
 * ---- The dependency, and the version it is pinned to ----
 *
 * SheetJS, installed from `cdn.sheetjs.com` rather than npm. The npm registry's last publish is
 * `xlsx@0.18.5`, which carries a known prototype-pollution advisory (CVE-2023-30533, fixed in
 * 0.19.3) among others. This code parses spreadsheets that arrive as EMAIL ATTACHMENTS from
 * outside the company — untrusted input is the one place a parser advisory actually matters — so
 * the patched 0.20.3 from the vendor's own distribution channel is used instead. It adds no
 * advisories of its own (`npm audit`: identical totals before and after).
 *
 * The cost of that choice, stated plainly: `npm install` now needs to reach `cdn.sheetjs.com`. If
 * that is unacceptable for CI, vendor the tarball rather than dropping back to 0.18.5.
 *
 * Everything here is imported DYNAMICALLY by its callers, so none of it enters the bundle unless a
 * legacy file is actually opened.
 */

/**
 * The OLE2 compound-file signature every `.xls` starts with.
 *
 * Detection is on these bytes and never on the file extension: suppliers routinely email a genuine
 * `.xls` named `.xlsx` and the reverse, and a filename is a claim where the magic number is a fact.
 */
export const OLE2_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] as const

export function isLegacyXls(bytes: Uint8Array): boolean {
  if (bytes.length < OLE2_MAGIC.length) return false
  return OLE2_MAGIC.every((byte, i) => bytes[i] === byte)
}

/**
 * A cell, as the two consumers of this parser both understand it — the intersection of
 * `read-excel-file`'s `CellValue` (the preview) and `sheetExtract`'s `SheetCell` (the item
 * reader). Declared here rather than importing either, so this module owes nothing to the parser
 * it exists to work around.
 */
export type LegacyCell = string | number | boolean | Date | null

/** One parsed sheet, shaped so both the preview and the item reader can take it unchanged. */
export interface LegacySheet {
  name: string
  rows: LegacyCell[][]
}

/**
 * Parse a legacy workbook into the same `{name, rows}` shape the `.xlsx` path produces.
 *
 * `defval: null` and `raw: true` matter: the default would drop empty cells, collapsing a row's
 * columns leftwards so a table with a blank column silently misaligns — and every downstream
 * reader here (`buildSheetPreview`, `sheetExtract`'s header-by-shape detection) works on column
 * POSITION. Dates come back as `Date` objects, matching the other parser.
 */
export async function parseLegacyXls(bytes: Uint8Array): Promise<LegacySheet[]> {
  const XLSX = await import('xlsx')
  const workbook = XLSX.read(bytes, { type: 'array', cellDates: true })

  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name]
    // `header: 1` yields arrays-of-cells rather than objects keyed by a guessed header row — the
    // header detection downstream is far better informed than anything this call could do.
    const rows = (sheet ? XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, raw: true, blankrows: true }) : []) as unknown[][]
    return {
      name,
      // `undefined` is not a `CellValue`; the other parser sends `null` for an empty cell.
      rows: rows.map((row) => row.map((cell) => (cell === undefined ? null : cell))) as LegacyCell[][],
    }
  })
}
